import { PageHero } from '@/components/PageHero';
import { FullContactForm } from '@/components/ContactForm';
import { FaqAccordion } from '@/components/FaqAccordion';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/components/Seo';
import { STATIC_IT } from '@/data/staticIt';
import { STATIC_EN } from '@/data/staticEn';
import { useLang } from '@/i18n/LangContext';
import { ArrowRight, CalendarCheck2, Clock3, FileCheck2, Info, Languages, MessageSquare, Video } from 'lucide-react';
import { LangLink } from '@/i18n/LangContext';

const ICONS = { MessageSquare, Clock3, FileCheck2, Video };

export default function Contatti() {
  const { lang, t } = useLang();
  const c = lang === 'en' ? STATIC_EN.contatti : STATIC_IT.contatti;
  useSeo({ ...c.seo, faq: c.faq });

  return (
    <div data-testid="contatti-page">
      <PageHero
        eyebrow={c.eyebrow}
        title={c.title}
        summary={c.summary}
        image="/assets/home-hero.webp"
        imageAlt={c.title}
        breadcrumbs={[{ label: t('breadcrumbHome'), href: '/' }, { label: c.eyebrow }]}
      />

      <section className="mx-auto max-w-site px-5 pt-12 md:px-8 md:pt-16" aria-labelledby="contact-choice-title">
        <Reveal>
          <div className="rounded-xl border border-border bg-white p-6 md:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">{lang === 'en' ? 'Choose how to continue' : 'Scegli come continuare'}</p>
            <h2 id="contact-choice-title" className="mt-3 font-heading text-2xl font-bold tracking-tight md:text-3xl">{lang === 'en' ? 'Book a meeting or ask for information.' : 'Prenota un appuntamento oppure chiedi informazioni.'}</h2>
            <div className="mt-7 grid gap-4 md:grid-cols-2">
              <LangLink to="/prenota" data-testid="contact-booking-choice" className="group rounded-xl bg-brand-deep p-6 text-white transition-colors hover:bg-primary">
                <CalendarCheck2 className="h-7 w-7 text-brand-light" />
                <h3 className="mt-5 font-heading text-xl font-bold">{lang === 'en' ? 'Book an appointment' : 'Prenota un appuntamento'}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/70">{lang === 'en' ? 'Choose an available date and time.' : 'Scegli direttamente il giorno e l’orario disponibili.'}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-brand-light">{lang === 'en' ? 'Book now' : 'Prenota ora'} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </LangLink>
              <a href="#richiesta-informazioni" data-testid="contact-info-choice" className="group rounded-xl border border-primary/20 bg-brand-pale p-6 transition-colors hover:border-primary">
                <Info className="h-7 w-7 text-primary" />
                <h3 className="mt-5 font-heading text-xl font-bold">{lang === 'en' ? 'Ask for information' : 'Chiedi informazioni'}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground/65">{lang === 'en' ? 'Use the form for questions, documents or a first contact.' : 'Usa il modulo per domande, documenti o un primo contatto.'}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-primary">{lang === 'en' ? 'Go to the form' : 'Vai al modulo'} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      <section id="richiesta-informazioni" className="mx-auto max-w-site px-5 md:px-8 py-14 md:py-20 grid lg:grid-cols-12 gap-12 lg:gap-16 scroll-mt-28">
        <div className="lg:col-span-5">
          <Reveal>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-8">{c.expectTitle}</h2>
            <div className="space-y-7">
              {c.expectations.map((item) => {
                const Icon = ICONS[item.icon];
                return (
                  <div key={item.title} className="flex items-start gap-4">
                    <span className="bg-brand-pale p-3 rounded-lg shrink-0">
                      <Icon className="h-5 w-5 text-primary" />
                    </span>
                    <div>
                      <h3 className="font-heading text-lg font-bold tracking-tight mb-1">{item.title}</h3>
                      <p className="text-sm md:text-base leading-relaxed text-foreground/70">{item.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-10 border-l-4 border-primary bg-brand-pale p-5 rounded-r-lg flex items-start gap-3">
              <Languages className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed text-foreground/80">
                {c.langNote}
              </p>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="bg-white border border-border p-6 md:p-10 rounded-lg">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-primary">{lang === 'en' ? 'Information only' : 'Richiesta informazioni'}</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-8">{c.formTitle}</h2>
              <FullContactForm />
            </div>
          </Reveal>
        </div>
      </section>

      <div className="mx-auto max-w-site px-5 md:px-8 pb-20">
        <FaqAccordion items={c.faq} eyebrow={c.faqEyebrow} title={c.faqTitle} />
      </div>
    </div>
  );
}
